import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import { fetchTasksMe, isTasksLeaderRole, tasksApi } from '../../../Tasks/tasksApi';
import { Badge, Button, EmptyState, Field, Loader, Select, Table, TextInput } from '../../../ui';
import type { BadgeTone } from '../../../ui';
import { Callout } from './ui-pending';
import './RmSection.scss';

/**
 * Relationship Manager section on a student's profile: who owns this
 * learner, the controls to give them one or move them, and the trail of
 * every change.
 *
 * The trail is the reason this is a section rather than a button. Which RM
 * owns a learner is a fact people act on and occasionally argue about, and
 * until now the profile showed the current answer with no way to ask how it
 * got that way.
 */

export interface RmSectionProfile {
  /** The learner, as the tasks service knows them (CMS userId). */
  studentExternalId: string;
  name: string;
  /** E.164, when the profile has both halves of it. */
  phone?: string;
  email?: string;
  /** IANA only — the profile form also holds an internal id in this field. */
  timezone?: string;
  credentialId?: string;
  studentId?: string;
}

interface AgentOption {
  externalId: string;
  name: string;
  role: string;
  isActive: boolean;
  availability?: 'ON_SHIFT' | 'OFF_SHIFT' | 'ON_LEAVE';
  onLeaveUntil?: string | null;
}

interface HistoryEntry {
  id: string;
  at: string;
  from: { externalId: string; name: string } | null;
  to: { externalId: string; name: string } | null;
  source: 'AUTO' | 'MANUAL';
  by: { externalId: string; name: string } | null;
  reason: string | null;
}

const when = (iso: string): string =>
  new Date(iso).toLocaleString('en-GB', {
    day: '2-digit', month: '2-digit', year: 'numeric',
    hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false,
  }).replace(',', ',');

const AVAIL: Record<string, { label: string; tone: BadgeTone }> = {
  ON_SHIFT: { label: 'On shift', tone: 'success' },
  OFF_SHIFT: { label: 'Off shift', tone: 'neutral' },
  ON_LEAVE: { label: 'On leave', tone: 'danger' },
};

const RmSection: React.FC<{ profile: RmSectionProfile; onChanged?: () => void }> = ({
  profile,
  onChanged,
}) => {
  const [rm, setRm] = useState<{ externalId: string; name: string } | null | undefined>(undefined);
  const [history, setHistory] = useState<HistoryEntry[] | null>(null);
  const [agents, setAgents] = useState<AgentOption[]>([]);
  const [canChange, setCanChange] = useState(false);
  /** Provisioned as an RM at all — the service refuses everyone else. */
  const [isRmStaff, setIsRmStaff] = useState<boolean | undefined>(undefined);
  const [picked, setPicked] = useState('');
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    const id = profile.studentExternalId;
    if (!id) return;
    try {
      const r = await tasksApi<{ rm: { externalId: string; name: string } }>(
        `/v1/students/${id}/rm`,
      );
      setRm(r?.rm ?? null);
    } catch (err) {
      // 404 is the ordinary answer for a learner nobody owns yet.
      setRm((err as { status?: number })?.status === 404 ? null : undefined);
    }
    try {
      setHistory(await tasksApi<HistoryEntry[]>(`/v1/students/${id}/rm-history`));
    } catch {
      setHistory([]);
    }
  }, [profile.studentExternalId]);

  useEffect(() => { void load(); }, [load]);

  useEffect(() => {
    fetchTasksMe().then((me) => {
      const leader = isTasksLeaderRole(me?.role);
      setIsRmStaff(!!me);
      setCanChange(leader);
      if (!leader) return;
      tasksApi<AgentOption[]>('/v1/agents')
        .then((rows) => setAgents(rows.filter((a) => a.role === 'AGENT' && a.isActive)))
        .catch(() => setAgents([]));
    });
  }, []);

  /** Everything the tasks service should keep about this learner. Sent on
   *  every write, because the mapping endpoint clears phone and email when
   *  they're absent rather than leaving them alone. */
  const snapshot = useMemo(
    () => ({
      ...(profile.phone ? { phone: profile.phone } : {}),
      ...(profile.email ? { email: profile.email } : {}),
      ...(profile.timezone && /^[A-Za-z]+\/[A-Za-z0-9_+\-/]+$/.test(profile.timezone)
        ? { timezone: profile.timezone }
        : {}),
      metadata: {
        ...(profile.credentialId ? { credentialId: profile.credentialId } : {}),
        ...(profile.studentId ? { studentId: profile.studentId } : {}),
      },
    }),
    [profile],
  );

  const assign = async () => {
    setBusy(true);
    try {
      const res = await tasksApi<{ agent: { externalId: string; name: string } }>(
        `/v1/students/${profile.studentExternalId}/assign-rm`,
        {
          method: 'POST',
          body: JSON.stringify({
            name: profile.name,
            ...snapshot,
            ...(reason.trim() ? { reason: reason.trim() } : {}),
          }),
        },
      );
      toast.success(`Assigned to ${res?.agent?.name}`);
      setReason('');
      await load();
      onChanged?.();
    } catch (err) {
      toast.error((err as Error)?.message || 'Could not assign an RM');
    } finally {
      setBusy(false);
    }
  };

  const change = async () => {
    if (!picked) return toast.error('Pick an RM first');
    setBusy(true);
    try {
      await tasksApi(`/v1/agent-student-maps`, {
        method: 'POST',
        body: JSON.stringify({
          studentExternalId: profile.studentExternalId,
          studentName: profile.name,
          studentPhoneE164: profile.phone,
          studentEmail: profile.email,
          ...(snapshot.timezone ? { timezone: snapshot.timezone } : {}),
          metadata: snapshot.metadata,
          agentExternalId: picked,
          ...(reason.trim() ? { reason: reason.trim() } : {}),
        }),
      });
      toast.success('RM changed');
      setPicked('');
      setReason('');
      await load();
      onChanged?.();
    } catch (err) {
      toast.error((err as Error)?.message || 'Could not change the RM');
    } finally {
      setBusy(false);
    }
  };

  const pickedAgent = agents.find((a) => a.externalId === picked);
  const pickedAvail = pickedAgent?.availability ? AVAIL[pickedAgent.availability] : undefined;

  return (
    <div className="rms ds-stack">
      {/* Who owns them now */}
      <div className="rm-row">
        <span className="ds-text-muted rms-label">Relationship Manager</span>
        {rm === undefined ? (
          <Loader label="Loading RM…" />
        ) : rm ? (
          <span className="ds-strong rms-owner">{rm.name}</span>
        ) : (
          <span className="ds-text-subtle rms-none">Nobody yet</span>
        )}
      </div>

      {/* Give them one, or move them */}
      <div className="rms-panel">
        {!rm ? (
          isRmStaff === false ? (
            <Callout tone="neutral">
              Assigning an RM is for the RM team — you don&apos;t have an RM record
              in this tool.
            </Callout>
          ) : (
            // Every region has a desk now, so this is offered for every
            // learner. It used to be hidden behind a +1 phone check, because
            // the service refused anyone the US/Canada desk didn't cover.
            <div className="rms-form">
              <Field label="Reason (optional)" className="rms-form__grow">
                {(f) => (
                  <TextInput {...f} size="sm" value={reason} placeholder="Why this learner needs an RM"
                    onChange={(e) => setReason(e.target.value)} />
                )}
              </Field>
              <Button variant="primary" size="sm" disabled={busy}
                onClick={() => void assign()}>
                {busy ? 'Assigning…' : 'Assign RM'}
              </Button>
            </div>
          )
        ) : canChange ? (
          <div className="rms-form">
            <Field label="Move to" className="rms-form__pick">
              {(f) => (
                <Select {...f} size="sm" value={picked}
                  onChange={(e) => setPicked(e.target.value)}
                  options={[
                    { value: '', label: 'Choose an RM…' },
                    ...agents
                      .filter((a) => a.externalId !== rm.externalId)
                      .map((a) => ({
                        value: a.externalId,
                        label: `${a.name}${a.availability === 'ON_LEAVE' ? ' — on leave' : ''}`,
                      })),
                  ]} />
              )}
            </Field>
            {pickedAvail && <Badge tone={pickedAvail.tone}>{pickedAvail.label}</Badge>}
            <Field label="Reason (optional)" className="rms-form__grow">
              {(f) => (
                <TextInput {...f} size="sm" value={reason} placeholder="Why they're being moved"
                  onChange={(e) => setReason(e.target.value)} />
              )}
            </Field>
            <Button variant="primary" size="sm" disabled={busy || !picked}
              onClick={() => void change()}>
              {busy ? 'Moving…' : 'Change RM'}
            </Button>
          </div>
        ) : (
          <p className="ds-text-muted rms-note">
            Only a team lead or admin can move a learner to a different RM.
          </p>
        )}
      </div>

      {/* How it got this way */}
      <div>
        <h3 className="rms-heading">History</h3>
        <p className="rm-caption rms-note">
          Every change of RM for this learner, newest first.
        </p>
      </div>
      {history === null ? (
        <Loader label="Loading history…" />
      ) : history.length === 0 ? (
        <EmptyState title="No changes recorded" description="RM changes for this learner will appear here." />
      ) : (
        <Table caption="RM history">
          <thead>
            <tr>
              <th>When</th>
              <th>From</th>
              <th>To</th>
              <th>Changed by</th>
              <th>Reason</th>
            </tr>
          </thead>
          <tbody>
            {history.map((h) => (
              <tr key={h.id}>
                <td className="rms-nowrap">{when(h.at)}</td>
                <td>{h.from ? h.from.name : <span className="ds-text-subtle">—</span>}</td>
                <td>
                  {h.to ? (
                    <span className="ds-strong">{h.to.name}</span>
                  ) : (
                    <Badge tone="danger">Unassigned</Badge>
                  )}
                </td>
                <td>
                  {h.by ? (
                    h.by.name
                  ) : (
                    <span title="Chosen by the assignment engine, not a person">
                      <Badge tone="neutral">{h.source === 'AUTO' ? 'Auto-assigned' : 'System'}</Badge>
                    </span>
                  )}
                </td>
                <td className="ds-text-muted">{h.reason || '—'}</td>
              </tr>
            ))}
          </tbody>
        </Table>
      )}
    </div>
  );
};

export default RmSection;
